import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { useSettingActions } from 'src/store/settings/actions';
import { RootState } from 'src/store/reducers/state';
import { uploadImageToS3 } from 'src/components/utilities/utils';
import { successMsg, errorMsg } from 'src/components/utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
import Loader from 'src/components/loader';

const CertificateEditor = () => {
  const dispatch = useDispatch();
  const settingAction = useSettingActions(dispatch);
  const { id } = useParams<string>();

  const signatureInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const [certificateData, setCertificateData] = useState({
    certificateTemplateName: 'Certificate Template',
    mainHeaderText: 'Certificate of Completion',
    subHeaderText: '',
    providerInformation:
      'This certificate is awarded for successful completion of the course requirements.',
    signatureName: 'John Doe',
    signatureTitle: 'Director of Education',
    signatureUrl: '',
    backgroundImageUrl: '',
    certificateBannerImage: ''
  });

  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [signatureFileName, setSignatureFileName] = useState('');
  const [backgroundFileName, setBackgroundFileName] = useState('');
  const [bannerFileName, setBannerFileName] = useState('');

  const { singleCertificateTemplate, isCertificateTemplateAdded } = useSelector((state: RootState) => {
    return {
      singleCertificateTemplate: state.setting.singleCertificateTemplate,
      isCertificateTemplateAdded: state.setting.isCertificateTemplateAdded
    };
  });

  const [previewData, setPreviewData] = useState({
    userName: 'Student name',
    title: 'Course name',
    completionDate: new Date().toLocaleDateString(),
    companyName: 'Your Company',
    categories: ['Web Development', 'React', 'JavaScript'],
    creditPoints: '10 CPD Points',
    certificateId: 'CERT-2024-001'
  });

  useEffect(() => {
    if (singleCertificateTemplate) {
      setCertificateData({
        certificateTemplateName:
          singleCertificateTemplate.certificateTemplateName || 'Certificate Template',
        mainHeaderText:
          singleCertificateTemplate.mainHeaderText || 'Certificate of Completion',
        subHeaderText: singleCertificateTemplate.subHeaderText || '',
        providerInformation:
          singleCertificateTemplate.providerInformation ||
          'This certificate is awarded for successful completion of the course requirements.',
        signatureName: singleCertificateTemplate.signatureName || 'John Doe',
        signatureTitle: singleCertificateTemplate.signatureTitle || 'Director of Education',
        signatureUrl: singleCertificateTemplate.signatureUrl || '',
        backgroundImageUrl: singleCertificateTemplate.backgroundImageUrl || '',
        certificateBannerImage: singleCertificateTemplate.certificateBannerImage || ''
      });
    }
  }, [singleCertificateTemplate]);

  useEffect(() => {
    if (id) {
      settingAction.getSingleCertificateTemplatesRequest(id);
    }
  }, [id, uploadSuccess]);

  const handleInputChange = (field: any, value: any) => {
    setCertificateData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (field: string, event: any, imageType: string) => {
    const file = event.target.files[0];
    if (file && id) {
      try {
        setUploadSuccess('');

        if (imageType === 'signatureurl') {
          setSignatureFileName(file.name);
        } else if (imageType === 'backgroundimageurl') {
          setBackgroundFileName(file.name);
        } else if (imageType === 'certificatebannerimage') {
          setBannerFileName(file.name);
        }

        await uploadImageToS3(
          file,
          `v2/member/certificate/upload/${id}?imageType=${imageType}&`,
          (url: string) => {
            handleInputChange(field, url);
            updateCertificateImageInApi(field, url, file.name);
          },
          'certificate',
          setIsUploadLoading,
          setUploadSuccess
        );
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadSuccess('fail');
      }
    }
  };

  const updateCertificateImageInApi = (field: string, imageUrl: string, name: string) => {
    if (id) {
      console.log(`Updating ${field} with URL: ${imageUrl}`);
      if (field === 'backgroundImageUrl') {
        const payloadData = {
          id,
          updateData: [{ op: 'replace', path: 'backgroundImageUrl', value: name }]
        };
        settingAction.updateCertificateTemplateRequest(payloadData);
      } else if (field === 'certificateBannerImage') {
        const payloadData = {
          id,
          updateData: [{ op: 'replace', path: 'certificateBannerImage', value: name }]
        };
        settingAction.updateCertificateTemplateRequest(payloadData);
      } else {
        const payloadData = {
          id,
          updateData: [{ op: 'replace', path: 'signatureUrl', value: name }]
        };
        settingAction.updateCertificateTemplateRequest(payloadData);
      }
    }
  };
  const handleRemoveImage = (field: string, imageType: string) => {
    handleInputChange(field, '');

    if (imageType === 'signatureurl') {
      setSignatureFileName('');
    } else if (imageType === 'backgroundimageurl') {
      setBackgroundFileName('');
    } else if (imageType === 'certificatebannerimage') {
      setBannerFileName('');
    }
    updateCertificateImageInApi(field, '', '');
  };

  const handleSave = () => {
    if (id) {
      const payloadData = {
        id,
        updateData: [
          {
            op: 'replace',
            path: 'providerInformation',
            value: certificateData.providerInformation
          },
          {
            op: 'replace',
            path: 'signatureName',
            value: certificateData.signatureName
          },
          {
            op: 'replace',
            path: 'signatureTitle',
            value: certificateData.signatureTitle
          },
          {
            op: 'replace',
            path: 'certificateTemplateName',
            value: certificateData.certificateTemplateName
          },
          {
            op: 'replace',
            path: 'mainHeaderText',
            value: certificateData.mainHeaderText
          },
          {
            op: 'replace',
            path: 'subHeaderText',
            value: certificateData.subHeaderText
          }
        ]
      };
      settingAction.updateCertificateTemplateRequest(payloadData);
    }
  };

  const handleDiscard = () => {
    if (singleCertificateTemplate) {
      setCertificateData({
        certificateTemplateName:
          singleCertificateTemplate.certificateTemplateName || 'Certificate Template',
        mainHeaderText:
          singleCertificateTemplate.mainHeaderText || 'Certificate of Completion',
        subHeaderText: singleCertificateTemplate.subHeaderText || '',
        providerInformation:
          singleCertificateTemplate.providerInformation ||
          'This certificate is awarded for successful completion of the course requirements.',
        signatureName: singleCertificateTemplate.signatureName || 'John Doe',
        signatureTitle: singleCertificateTemplate.signatureTitle || 'Director of Education',
        signatureUrl: singleCertificateTemplate.signatureUrl || '',
        backgroundImageUrl: singleCertificateTemplate.backgroundImageUrl || '',
        certificateBannerImage: singleCertificateTemplate.certificateBannerImage || ''
      });
    }
    setSignatureFileName('');
    setBackgroundFileName('');
    setBannerFileName('');
  };
  useEffect(() => {
    if (uploadSuccess === 'success') {
      successMsg('Image uploaded successfully');
      setUploadSuccess('');
    } else if (uploadSuccess === 'fail') {
      errorMsg('Image upload failed');
      setUploadSuccess('');
    }
  }, [uploadSuccess]);

  useEffect(() => {
    if (isCertificateTemplateAdded) {
      successMsg('Success');
      settingAction.resetSettingStateRequest()
    }
  }, [isCertificateTemplateAdded]);

  const onInputClick = (event: any) => {
    event.target.value = '';
  };

  return (
    <div>
      <div className="flex justify-between">
        <div>
          <Link to={`/settings/education`}>
            <i className="fas fa-arrow-left mt-2 mb-2"></i>
            <span className="ml-2">Go Back</span>
          </Link>
        </div>
        <div>
          <button
            onClick={handleDiscard}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50">
            DISCARD CHANGES
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 ml-5">
            SAVE
          </button>
        </div>
      </div>
      <div className="min-h-screen bg-gray-50 flex">
        {/* Header */}

        {/* Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 pt-20 overflow-y-auto">
          <div className="p-6">
            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 mb-6">
              <button className="px-4 py-2 text-sm text-indigo-600 border-b-2 border-indigo-600 font-medium">
                Content
              </button>
            </div>

            {/* Template Name */}
            <div className="mb-6">
              <label className="form-group mb-4">Certificate Template Name</label>
              <input
                type="text"
                value={certificateData.certificateTemplateName}
                onChange={(e) => handleInputChange('certificateTemplateName', e.target.value)}
                className="border input-style"
              />
            </div>

            {/* Main Heading Text */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main Heading Text
              </label>
              <input
                type="text"
                value={certificateData.mainHeaderText}
                onChange={(e) => handleInputChange('mainHeaderText', e.target.value)}
                placeholder="Certificate of Completion"
                className="border input-style"
              />
            </div>

            {/* Sub Heading Text */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sub Heading Text
              </label>
              <textarea
                value={certificateData.subHeaderText}
                onChange={(e) => handleInputChange('subHeaderText', e.target.value)}
                rows={2}
                placeholder="Optional sub heading text"
                className="border input-style"
              />
            </div>

            {/* Provider Information */}
            <div className="mb-6">
              <label className="form-group mb-4">Provider Information</label>
              <textarea
                value={certificateData.providerInformation}
                onChange={(e) => handleInputChange('providerInformation', e.target.value)}
                rows={4}
                className="border input-style"
              />
            </div>

            {/* Signature Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Signature Name</label>
              <input
                type="text"
                value={certificateData.signatureName}
                onChange={(e) => handleInputChange('signatureName', e.target.value)}
                className="border input-style"
              />
            </div>

            {/* Signature Title */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature Title
              </label>
              <input
                type="text"
                value={certificateData.signatureTitle}
                onChange={(e) => handleInputChange('signatureTitle', e.target.value)}
                className="border input-style"
              />
            </div>

            {/* Signature Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature Image
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload('signatureUrl', e, 'signatureurl')}
                  className="hidden"
                  id="signature-upload"
                  ref={signatureInputRef}
                  onClick={onInputClick}
                />
                <label htmlFor="signature-upload" className="cursor-pointer">
                  {/* <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" /> */}
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-indigo-600 hover:text-indigo-500">
                      Click to upload
                    </span>{' '}
                    signature image
                  </div>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                </label>
              </div>
              {certificateData.signatureUrl && (
                <div className="mt-2">
                  <img
                    src={certificateData.signatureUrl}
                    alt="Signature preview"
                    className="h-16 w-auto border rounded"
                  />
                  <button
                    onClick={() => handleRemoveImage('signatureUrl', 'signatureurl')}
                    className="mt-1 text-xs text-red-600 hover:text-red-800">
                    Remove Image
                  </button>
                </div>
              )}
              {signatureFileName && (
                <p className="text-xs text-gray-500 mt-1">File: {signatureFileName}</p>
              )}
            </div>

            {/* Background Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Image
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload('backgroundImageUrl', e, 'backgroundimageurl')}
                  className="hidden"
                  id="background-upload"
                  ref={backgroundInputRef}
                  onClick={onInputClick}
                />
                <label htmlFor="background-upload" className="cursor-pointer">
                  {/* <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" /> */}
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-indigo-600 hover:text-indigo-500">
                      Click to upload
                    </span>{' '}
                    background image
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 1920 x 1200 px or larger (16:10 aspect ratio)
                  </p>
                </label>
              </div>
              {certificateData.backgroundImageUrl && (
                <div className="mt-2">
                  <img
                    src={certificateData.backgroundImageUrl}
                    alt="Background preview"
                    className="h-16 w-auto border rounded"
                  />
                  <button
                    onClick={() => handleRemoveImage('backgroundImageUrl', 'backgroundimageurl')}
                    className="mt-1 text-xs text-red-600 hover:text-red-800">
                    Remove Image
                  </button>
                </div>
              )}
              {backgroundFileName && (
                <p className="text-xs text-gray-500 mt-1">File: {backgroundFileName}</p>
              )}
            </div>

            {/* Certificate Banner Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Certificate Banner Image (Logo)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageUpload('certificateBannerImage', e, 'certificatebannerimage')
                  }
                  className="hidden"
                  id="banner-upload"
                  ref={bannerInputRef}
                  onClick={onInputClick}
                />
                <label htmlFor="banner-upload" className="cursor-pointer">
                  {/* <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" /> */}
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-indigo-600 hover:text-indigo-500">
                      Click to upload
                    </span>{' '}
                    logo/banner
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Recommended height: 120px</p>
                  <p className="text-xs font-semibold">
                    (width scales automatically to maintain aspect Ratio)
                  </p>
                </label>
              </div>
              {certificateData.certificateBannerImage && (
                <div className="mt-2">
                  <img
                    src={certificateData.certificateBannerImage}
                    alt="Banner preview"
                    className="h-16 w-auto border rounded"
                  />
                  <button
                    onClick={() =>
                      handleRemoveImage('certificateBannerImage', 'certificatebannerimage')
                    }
                    className="mt-1 text-xs text-red-600 hover:text-red-800">
                    Remove Image
                  </button>
                </div>
              )}
              {bannerFileName && (
                <p className="text-xs text-gray-500 mt-1">File: {bannerFileName}</p>
              )}
            </div>
          </div>
        </div>

        {/* Certificate Preview */}
        <div className="flex-1 pt-20 p-8">
          <div className="flex justify-center">
            <div
              className="bg-white rounded-lg shadow-lg overflow-hidden"
              style={{ width: '1150px', aspectRatio: '16 / 9' }}>
              {/* Certificate Content */}
              <div
                className="relative w-full h-full flex flex-col items-center justify-center p-8"
                style={{
                  backgroundImage: certificateData.backgroundImageUrl
                    ? `url(${certificateData.backgroundImageUrl})`
                    : 'none',
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundColor: certificateData.backgroundImageUrl ? 'transparent' : '#f9fafb'
                }}>
                {/* Border (only show if no background image) */}
                {!certificateData.backgroundImageUrl && (
                  <div className="absolute inset-0 border-8 border-gray-400 m-2">
                    <div className="absolute inset-2 border border-gray-400"></div>
                  </div>
                )}

                {/* Company Logo */}
                {certificateData.certificateBannerImage && (
                  <div className="mb-8">
                    <img
                      src={certificateData.certificateBannerImage}
                      alt="Company Logo"
                      className="h-16 w-auto max-w-80 object-contain"
                    />
                  </div>
                )}

                {/* Certificate Main Heading */}
                <h1 className="text-2xl font-bold text-gray-800 mb-1 tracking-wider">
                  {(certificateData.mainHeaderText || 'Certificate of Completion').toUpperCase()}
                </h1>

                {/* Certificate Sub Heading */}
                {certificateData.subHeaderText && (
                  <p className="text-sm text-gray-500 mb-2 max-w-2xl text-center">
                    {certificateData.subHeaderText}
                  </p>
                )}

                <p className="text-sm text-gray-600 mb-1 tracking-wide">THIS CERTIFIES THAT</p>

                {/* Student Name */}
                <h2 className="text-3xl font-bold text-red-600 mb-4 tracking-wide">
                  {previewData.userName.toUpperCase()}
                </h2>

                <p className="text-sm text-gray-600 mb-1">Has successfully completed</p>

                {/* Course Name */}
                <h3 className="text-xl font-bold text-red-600 mb-1">{previewData.title}</h3>

                <p className="text-sm text-gray-600 italic mb-2">By: John Smith</p>

                {/* Issue Date */}
                <div className="flex justify-between w-full px-56 mt-[-220px] mb-5">
                  <p className="text-sm text-gray-500">COMPLETION DATE: 2023-07-12</p>
                  <p className="text-sm text-gray-500">CREDITS: 1.0 CPD</p>
                </div>

                {/* Categories/Skills */}
                <div className="flex gap-2 mb-8">
                  {previewData.categories.slice(0, 3).map((category, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-200 text-gray-700 text-xs ">
                      {category}
                    </span>
                  ))}
                </div>

                {/* Bottom Section */}
                <div className=" px-12 ">
                  <div className="flex justify-between items-end">
                    {/* Signature Section */}
                    <div className="flex-1">
                      {certificateData.signatureUrl && (
                        <div className="mb-2">
                          <img
                            src={certificateData.signatureUrl}
                            alt="Signature"
                            className="h-12 w-auto"
                          />
                        </div>
                      )}
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-800">
                          {certificateData.signatureName || 'Signature Name'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {certificateData.signatureTitle || 'Signature Title'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Provider Information */}
                  <div className="text-center mt-8">
                    <p className="text-xs text-gray-800">Company Name</p>
                    <p className="text-xs text-gray-600 max-w-md mx-auto">
                      {certificateData.providerInformation || 'Provider information not available'}
                    </p>
                  </div>

                  {/* Certificate ID */}
                  <div className="absolute bottom-8 right-8">
                    <p className="text-xs text-gray-500">
                      Certificate ID: {previewData.certificateId}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading overlay */}
        {isUploadLoading && <Loader />}

        {/* Toast messages */}
        <ToastMessage />
      </div>
    </div>
  );
};

export default CertificateEditor;